import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleCheck, Undo2, Printer } from "lucide-react";
import { download } from "@/lib/api";
import { MailButton } from "@/components/MailButton";
import { api, eur, dt, errMsg } from "@/lib/api";
import { useAuth, isAgent } from "@/context/AuthContext";
import { PageHeader, Panel, Empty } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const METODI = ["Contanti", "Assegno", "Bonifico", "POS"];
const today = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const Riga = ({ i, children, showBy }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-3 last:border-0">
    <Link to={`/app/fatture/${i.id}`} className="min-w-[90px] font-mono text-sm font-semibold text-sky-700 hover:underline">{i.numero}{i.rata && <span data-testid={`incassi-rata-${i.key}`} className="ml-1 rounded bg-sky-100 px-1 text-[10px] font-semibold text-sky-800">rata {i.rata}</span>}</Link>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium text-slate-900">{i.controparte?.denominazione}</div>
      <div className="text-xs text-slate-500">del {dt(i.data)}{i.scadenza ? ` · scade ${dt(i.scadenza)}` : ""}{i.incasso_metodo ? ` · ${i.incasso_metodo}` : ""}{showBy && i.incasso_by ? ` · ${i.incasso_by}` : ""}</div>
    </div>
    <div className="font-mono text-base font-bold text-slate-900">{eur(i.totals.totale)}</div>
    {children}
  </div>
);

const stampa = (giorno, email) => download("/invoices/incassi/pdf", { giorno, email }).catch((e) => toast.error(e.message));

const PerUtente = ({ rows, giorno }) => (
  <Panel title="Incassato da ogni agente" className="mb-6" action={rows.length > 0 && <Button size="sm" variant="outline" data-testid="incassi-print-all" onClick={() => stampa(giorno)}><Printer className="mr-1 h-4 w-4" />Stampa tutti</Button>}>
    {rows.length === 0 ? <Empty text="Nessun incasso registrato in questa giornata" /> : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="incassi-per-agente">
        {rows.map((r) => (
          <div key={r.email || "nd"} data-testid={`incassi-agente-${r.email || "nd"}`} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">{r.nome}</div>
              <div className="text-xs text-slate-500">{r.agente ? "Agente di vendita" : "Ufficio"} · {r.numero} incassi</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-lg font-bold text-emerald-800">{eur(r.totale)}</div>
              <Button variant="ghost" size="icon" title="Stampa riepilogo PDF" data-testid={`incassi-print-${r.email || "nd"}`} onClick={() => stampa(giorno, r.email)}><Printer className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </Panel>
);

export default function Incassi() {
  const { user } = useAuth();
  const [giorno, setGiorno] = useState(today());
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [metodo, setMetodo] = useState("Contanti");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/invoices/incassi", { params: { giorno } }).then((r) => setD(r.data)), [giorno]);
  useEffect(() => { load(); }, [load]);
  const pay = async (i, pagata) => {
    setBusy(true);
    try {
      await api.post(i.rata_n ? `/invoices/${i.id}/rate/${i.rata_n - 1}/pay` : `/invoices/${i.id}/pay`, { pagata, data_pagamento: giorno, metodo });
      toast.success(pagata ? `Incasso registrato: ${i.numero} · ${eur(i.totals.totale)}` : "Incasso annullato");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const sospesi = d.sospesi.filter((i) => !q || `${i.numero} ${i.controparte?.denominazione}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHeader title="Incassi" subtitle={isAgent(user) ? "Registra gli incassi dei sospesi e controlla il totale della tua giornata" : "Fatture da incassare e incassi della giornata"}>
        <DateField testid="incassi-day-input" className="w-44 bg-white" value={giorno} max={today()} onChange={(v) => setGiorno(v || today())} />
        {isAgent(user) && <Button variant="outline" className="bg-white" data-testid="incassi-print-mine" disabled={d.incassati.length === 0} onClick={() => stampa(d.giorno)}><Printer className="mr-2 h-4 w-4" />Stampa riepilogo</Button>}
      </PageHeader>
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5"><div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Sospesi da incassare</div><div data-testid="incassi-total-open" className="mt-1 font-mono text-3xl font-bold text-amber-900">{eur(d.totale_sospesi)}</div><div className="text-xs text-amber-800">{d.sospesi.length} {d.sospesi.some((i) => i.rata) ? "scadenze" : "fatture"}</div></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5"><div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Incassato il {dt(d.giorno)}{isAgent(user) ? " da te" : ""}</div><div data-testid="incassi-total-day" className="mt-1 font-mono text-3xl font-bold text-emerald-900">{eur(d.totale_incassato)}</div><div className="text-xs text-emerald-800">{d.incassati.length} incassi</div></div>
      </div>
      {!isAgent(user) && <PerUtente rows={d.per_utente || []} giorno={d.giorno} />}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Sospesi">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <Input data-testid="incassi-search-input" placeholder="Cerca cliente o numero" className="bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select value={metodo} onValueChange={setMetodo}>
              <SelectTrigger data-testid="incassi-method-select" className="bg-white sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{METODI.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {sospesi.length === 0 ? <Empty text="Nessuna fattura da incassare" /> : sospesi.map((i) => (
            <Riga key={i.key || i.id} i={i}>
              <Button data-testid={`incassa-${i.key || i.id}`} disabled={busy} className="h-11 bg-emerald-600 px-5 hover:bg-emerald-700" onClick={() => pay(i, true)}><CircleCheck className="mr-2 h-4 w-4" />Incassa</Button>
            </Riga>
          ))}
        </Panel>
        <Panel title="Incassi della giornata">
          {d.incassati.length === 0 ? <Empty text="Nessun incasso registrato in questa giornata" /> : d.incassati.map((i) => (
            <Riga key={i.key || i.id} i={i} showBy={!isAgent(user)}>
              <MailButton icon path={`/invoices/${i.id}/email-incasso`} email={i.controparte?.email} what="la ricevuta di incasso" testid={`email-incasso-${i.id}`} />
              <Button variant="ghost" size="sm" data-testid={`annulla-incasso-${i.key || i.id}`} disabled={busy} onClick={() => pay(i, false)}><Undo2 className="mr-1 h-4 w-4" />Annulla</Button>
            </Riga>
          ))}
        </Panel>
      </div>
    </div>
  );
}
